import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  BlockStack,
  InlineStack,
  DataTable,
  Banner,
  Toast,
  Spinner,
  Collapsible,
} from "@shopify/polaris";
import {
  usePurchaseOrders,
  type PurchaseOrder,
} from "../hooks/usePurchaseOrders";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { formatCurrency, formatPercent } from "../utils/cost-calculations";
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch";

const STATUS_TONES: Record<string, "info" | "attention" | "success" | "critical" | undefined> = {
  draft: "info",
  submitted: "attention",
  partially_received: "attention",
  fully_received: "success",
  cancelled: "critical",
  closed: undefined,
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  submitted: "Submitted",
  partially_received: "Partially Received",
  fully_received: "Fully Received",
  cancelled: "Cancelled",
  closed: "Closed",
};

export default function PODetail() {
  const { poId } = useParams<{ poId: string }>();
  const navigate = useNavigate();

  const {
    loadPurchaseOrder,
    submitPurchaseOrder,
    cancelPurchaseOrder,
    deletePurchaseOrder,
    loadReceivingHistory,
    error,
  } = usePurchaseOrders();

  const [po, setPO] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [receivingHistory, setReceivingHistory] = useState<any[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const authFetch = useAuthenticatedFetch();

  useEffect(() => {
    if (poId) {
      setLoading(true);
      loadPurchaseOrder(poId).then((data) => {
        setPO(data);
        setLoading(false);
      });
      loadReceivingHistory(poId).then(setReceivingHistory);
    }
  }, [poId]);

  const handleSubmit = useCallback(async () => {
    if (!poId) return;
    setActionLoading(true);
    const success = await submitPurchaseOrder(poId);
    if (success) {
      const updated = await loadPurchaseOrder(poId);
      setPO(updated);
      setToastMessage("Purchase order submitted");
    }
    setActionLoading(false);
  }, [poId, submitPurchaseOrder, loadPurchaseOrder]);

  const handleCancel = useCallback(async () => {
    if (!poId) return;
    setActionLoading(true);
    const success = await cancelPurchaseOrder(poId);
    if (success) {
      const updated = await loadPurchaseOrder(poId);
      setPO(updated);
      setToastMessage("Purchase order cancelled");
    }
    setActionLoading(false);
  }, [poId, cancelPurchaseOrder, loadPurchaseOrder]);

  const handleDelete = useCallback(async () => {
    if (!poId) return;
    const success = await deletePurchaseOrder(poId);
    if (success) {
      navigate("/purchase-orders");
    }
    setDeleteModalOpen(false);
  }, [poId, deletePurchaseOrder, navigate]);

  const handleDownloadPdf = useCallback(async () => {
    if (!poId) return;
    try {
      const res = await authFetch(`/api/purchase-orders/${poId}/pdf`);
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${po?.poNumber || "PO"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setToastMessage("PDF downloaded");
    } catch (err: any) {
      setToastMessage("Failed to generate PDF");
    }
  }, [poId, po, authFetch]);

  const handleExportExcel = useCallback(async () => {
    if (!poId) return;
    try {
      const res = await authFetch(`/api/purchase-orders/${poId}/export/excel`);
      if (!res.ok) throw new Error("Excel export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${po?.poNumber || "PO"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setToastMessage("Excel exported");
    } catch (err: any) {
      setToastMessage("Failed to export Excel");
    }
  }, [poId, po, authFetch]);

  const handleEmailPO = useCallback(async () => {
    if (!poId) return;
    setActionLoading(true);
    try {
      const res = await authFetch(`/api/purchase-orders/${poId}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setToastMessage(`Email sent to ${json.data?.recipient}`);
    } catch (err: any) {
      setToastMessage(err.message || "Failed to send email");
    }
    setActionLoading(false);
  }, [poId, authFetch]);

  const handlePrint = useCallback(async () => {
    if (!poId) return;
    try {
      const res = await authFetch(`/api/purchase-orders/${poId}/pdf`);
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setToastMessage("Print preview opened");
    } catch (err: any) {
      setToastMessage("Failed to generate print preview");
    }
  }, [poId, authFetch]);

  // Must be before the early return to maintain consistent hook count
  const handleLineItemClick = useCallback(
    (variantId: string) => {
      if (po && po.status === "draft") {
        navigate(`/purchase-orders/${po.id}/edit?scrollTo=${variantId}`);
      }
    },
    [po, navigate]
  );

  if (loading || !po) {
    return (
      <Page title="Loading...">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const isDraft = po.status === "draft";
  const isSubmitted = po.status === "submitted";

  const secondaryActions = [];
  if (isDraft) {
    secondaryActions.push({
      content: "Edit in Builder",
      url: `/purchase-orders/${po.id}/edit`,
    });
    secondaryActions.push({
      content: "Submit",
      onAction: handleSubmit,
      loading: actionLoading,
    });
    secondaryActions.push({
      content: "Download PDF",
      onAction: handleDownloadPdf,
    });
    secondaryActions.push({
      content: "Export Excel",
      onAction: handleExportExcel,
    });
    secondaryActions.push({
      content: "Delete",
      destructive: true,
      onAction: () => setDeleteModalOpen(true),
    });
  }
  if (isSubmitted || po.status === "partially_received") {
    secondaryActions.push({
      content: "Receive Shipment",
      url: `/purchase-orders/${po.id}/receive`,
    });
    secondaryActions.push({
      content: "Cancel PO",
      destructive: true,
      onAction: handleCancel,
      loading: actionLoading,
    });
    secondaryActions.push({
      content: "Email PO",
      onAction: handleEmailPO,
      loading: actionLoading,
    });
    secondaryActions.push({
      content: "Download PDF",
      onAction: handleDownloadPdf,
    });
    secondaryActions.push({
      content: "Print",
      onAction: handlePrint,
    });
    secondaryActions.push({
      content: "Export Excel",
      onAction: handleExportExcel,
    });
  }

  return (
    <>
      <Page
        title={po.poNumber}
        titleMetadata={
          <Badge tone={STATUS_TONES[po.status]}>
            {STATUS_LABELS[po.status] || po.status}
          </Badge>
        }
        backAction={{ content: "Purchase Orders", url: "/purchase-orders" }}
        secondaryActions={secondaryActions}
      >
        {error && (
          <Layout.Section>
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout>
          {/* PO Info */}
          <Layout.Section>
            <Card>
              <InlineStack gap="800" wrap>
                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Vendor</Text>
                  <Text as="span" fontWeight="semibold">
                    {po.vendor?.name || "\u2014"}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Order Date</Text>
                  <Text as="span" fontWeight="semibold">
                    {po.orderDate ? new Date(po.orderDate).toLocaleDateString() : "\u2014"}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Expected Delivery</Text>
                  <Text as="span" fontWeight="semibold">
                    {po.expectedDelivery
                      ? new Date(po.expectedDelivery).toLocaleDateString()
                      : "\u2014"}
                  </Text>
                </BlockStack>
                {po.submittedAt && (
                  <BlockStack gap="100">
                    <Text as="span" tone="subdued">Submitted</Text>
                    <Text as="span" fontWeight="semibold">
                      {new Date(po.submittedAt).toLocaleDateString()}
                    </Text>
                  </BlockStack>
                )}
              </InlineStack>
              {po.notes && (
                <div style={{ marginTop: 12 }}>
                  <Text as="span" tone="subdued">Notes: </Text>
                  <Text as="span">{po.notes}</Text>
                </div>
              )}
            </Card>
          </Layout.Section>

          {/* Line Items */}
          <Layout.Section>
            <Card padding="0">
              <div style={{ padding: "16px 16px 8px" }}>
                <InlineStack align="space-between">
                  <Text variant="headingMd" as="h2">
                    Line Items ({po.lineItems?.length || 0})
                  </Text>
                  {isDraft && (
                    <Text as="span" variant="bodySm" tone="subdued">Click a row to edit quantity</Text>
                  )}
                </InlineStack>
              </div>
              {/* Header */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 80px 100px", padding: "8px 16px", borderBottom: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
                <Text as="span" variant="bodySm" fontWeight="semibold">Product</Text>
                <Text as="span" variant="bodySm" fontWeight="semibold" alignment="end">Ordered</Text>
                <Text as="span" variant="bodySm" fontWeight="semibold" alignment="end">Received</Text>
                <Text as="span" variant="bodySm" fontWeight="semibold" alignment="end">Line Total</Text>
              </div>
              {/* Rows */}
              {(po.lineItems || []).map((li, idx) => {
                const retailPrice = Number(li.retailPrice) || 0;
                const unitCost = Number(li.unitCost) || 0;
                const margin = retailPrice > 0 ? (retailPrice - unitCost) / retailPrice : 0;
                const productName = (li.productTitle || "") +
                  (li.variantTitle && li.variantTitle !== li.productTitle ? ` - ${li.variantTitle}` : "");

                return (
                  <div
                    key={li.id || idx}
                    onClick={() => isDraft && handleLineItemClick(String(li.shopifyVariantId))}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 80px 80px 100px",
                      padding: "10px 16px",
                      borderBottom: "1px solid #f0f0f0",
                      cursor: isDraft ? "pointer" : "default",
                      backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa",
                      alignItems: "start",
                    }}
                    onMouseEnter={(e) => { if (isDraft) e.currentTarget.style.backgroundColor = "#f0f4ff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "#fff" : "#fafafa"; }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "13px" }}>{productName}</div>
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                        {[
                          li.sku ? `SKU: ${li.sku}` : null,
                          `Pack: ${li.packSize}`,
                          `Case: ${formatCurrency(li.caseCost)}`,
                          `Net: ${formatCurrency(li.netCaseCost)}`,
                          `Unit: ${formatCurrency(li.unitCost)}`,
                          `Retail: ${formatCurrency(li.retailPrice)}`,
                          `Margin: ${formatPercent(margin)}`,
                        ].filter(Boolean).join(" | ")}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", fontWeight: 600 }}>{String(li.quantityOrdered)}</div>
                    <div style={{ textAlign: "right" }}>{String(li.quantityReceived)}</div>
                    <div style={{ textAlign: "right", fontWeight: 600 }}>{formatCurrency(li.lineTotal)}</div>
                  </div>
                );
              })}
            </Card>
          </Layout.Section>

          {/* Totals */}
          <Layout.Section>
            <Card>
              <InlineStack gap="800" align="end">
                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Subtotal</Text>
                  <Text as="span" fontWeight="bold" variant="headingMd">
                    {formatCurrency(po.subtotal)}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Tax</Text>
                  <Text as="span" fontWeight="bold" variant="headingMd">
                    {formatCurrency(po.tax)}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="span" tone="subdued">Total</Text>
                  <Text as="span" fontWeight="bold" variant="headingLg">
                    {formatCurrency(po.total)}
                  </Text>
                </BlockStack>
              </InlineStack>
            </Card>
          </Layout.Section>

          {/* Receiving History */}
          {receivingHistory.length > 0 && (
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between">
                    <Text variant="headingMd" as="h2">
                      Receiving History ({receivingHistory.length})
                    </Text>
                    <button
                      onClick={() => setHistoryOpen(!historyOpen)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--p-color-text-emphasis)",
                        textDecoration: "underline",
                      }}
                    >
                      {historyOpen ? "Hide" : "Show"}
                    </button>
                  </InlineStack>
                  <Collapsible open={historyOpen} id="receiving-history">
                    <BlockStack gap="400">
                      {receivingHistory.map((record: any) => (
                        <Card key={record.id}>
                          <BlockStack gap="200">
                            <InlineStack gap="400">
                              <Text as="span" fontWeight="semibold">
                                {new Date(record.receivedDate || record.createdAt).toLocaleDateString()}
                              </Text>
                              {record.invoiceNumber && (
                                <Text as="span" tone="subdued">
                                  Invoice: {record.invoiceNumber}
                                </Text>
                              )}
                              <Badge tone={record.shopifyInventorySynced ? "success" : "critical"}>
                                {record.shopifyInventorySynced ? "Synced" : "Not Synced"}
                              </Badge>
                            </InlineStack>
                            {record.lineItems?.map((rli: any) => (
                              <Text key={rli.id} as="p" tone="subdued">
                                {rli.poLineItem?.productTitle || rli.poLineItem?.sku || "Unknown"}: received {rli.quantityReceived}
                                {rli.quantityDamaged > 0 ? `, ${rli.quantityDamaged} damaged` : ""}
                                {rli.costVariance != null && rli.costVariance !== 0
                                  ? ` (variance: $${Number(rli.costVariance).toFixed(4)})`
                                  : ""}
                              </Text>
                            ))}
                          </BlockStack>
                        </Card>
                      ))}
                    </BlockStack>
                  </Collapsible>
                </BlockStack>
              </Card>
            </Layout.Section>
          )}
        </Layout>

        {deleteModalOpen && (
          <DeleteConfirmationModal
            open={deleteModalOpen}
            title={`Delete ${po.poNumber}?`}
            message="This will permanently delete this purchase order and all its line items."
            onConfirm={handleDelete}
            onCancel={() => setDeleteModalOpen(false)}
          />
        )}

        {toastMessage && (
          <Toast content={toastMessage} onDismiss={() => setToastMessage(null)} />
        )}
      </Page>
    </>
  );
}
